import cn from 'classnames';
import { useRouter } from 'next/router';
import type { FC } from 'react';
import { memo, useMemo } from 'react';

import { Button, Icon, Typography } from '@sravni/react-design-system';
import { Tip } from '@sravni/react-icons';
import { useIsMobile } from '@sravni/react-utils';

import type { CommonCardProps } from '@src/@types/commonCardProps';
import type { AffSub4 } from '@src/@types/microcredits';
import { useMediaQuery } from '@src/components/item/hooks';
import Link from '@src/components/Link';
import { Logo } from '@src/components/Logotype';
import { Pixel } from '@src/components/Pixel';
import { ContentBankruptcy } from '@src/components/ProductList/components/Card/ContentBankruptcy';
import { usePageContext } from '@src/config';
import { ListKeys } from '@src/constants/microcredits';
import { getAmountString, humanizeTermRange } from '@src/helpers/common';
import { getCurrency } from '@src/helpers/currency';
import { useIsReviewer } from '@src/reducers/metadata';
import { useIsPromoActive } from '@src/reducers/promo';
import { useHasUtmLabel } from '@src/reducers/route';
import { isLandingPage, isPartner2GisPage, isSpecialPageWithoutQuery } from '@src/utils/routing';
import { useCardAction } from 'entities/Microcredits/ui/CardActionProvider';
import { CardApprovalProbability, getCardBadge } from 'entities/Microcredits/ui/LightweightCard';
import { AdaptiveText } from 'shared/ui';

import { CardActionButton } from './CardActionButton';
import { ContentItem } from './ContentItem';
import styles from './styles.module.scss';
import { useCardClick } from './useCardClick';
import { getTemporaryTerm } from './utils';

const MIN_WIDTH_PX = 0;
const MAX_WIDTH_PX = 460;
const PARTNER_2GIS_SOURCE_HO_PARAM = '9810';

/**
 * todo: memo не работает, стоит либо убрать его, либо подумать как оптимизировать.
 * Обновляется пропс offer.advertising.advSub при каждом ререндере
 */
const Card: FC<CommonCardProps> = memo(
  // eslint-disable-next-line complexity,max-statements
  ({ className, offer, position, afterCard, actionButton, detailsButtonProps, onContentClickCapture }) => {
    const { name, organization, approvalProbability, _id } = offer;
    const { listKey } = usePageContext();
    const { asPath } = useRouter();
    const isSpecial = isSpecialPageWithoutQuery(asPath);
    const isMobile = useIsMobile();
    const isReviewer = useIsReviewer();
    const hasUtmLabel = useHasUtmLabel();
    const isLanding = isLandingPage(asPath);
    const isPartner2Gis = isPartner2GisPage(asPath);
    const isPromoActive = useIsPromoActive();

    const littleMode = useMediaQuery(MIN_WIDTH_PX, MAX_WIDTH_PX);

    const badge = useMemo(
      () =>
        getCardBadge({
          offer,
          hasUtmLabel,
          styles,
          settings: {
            hidePartnerBadge: isSpecial,
            hideSeoBadge: isSpecial,
          },
        }),
      [offer, hasUtmLabel, isSpecial],
    );

    const currency = getCurrency(asPath);
    const amount = getAmountString(offer.details?.amount?.to || offer.details?.amount?.from, currency);
    const term = humanizeTermRange(offer.details?.term, { useAlternativeFormat: true, termWithUnit: false });

    const { sourceLink, pixelDisplayLink, logoLink, cardLink, freeButtonLink } = useCardClick({
      offer,
      position,
    });

    const affSub4: AffSub4 = {
      organizationName: organization.name,
      productName: name,
      organizationId: organization._id,
      productId: _id,
    };

    const isTemporaryTerm = isReviewer && listKey === ListKeys.WL_PAGE;
    // const isAdvertisingInfoVisible = offer.advertising && !isSpecial && !isKZLanding;

    // для партнера 2GIS параметр source в ХО передаем определенное значение
    const sourceAffI = isPartner2Gis ? PARTNER_2GIS_SOURCE_HO_PARAM : `${sourceLink}|position_${position}`;
    const sourceAffC = isPartner2Gis ? PARTNER_2GIS_SOURCE_HO_PARAM : `${sourceLink}|position${position}`;

    const isBankruptcyCard = offer?._features?.isBankruptcyOffer;

    const isFullDealCard = useMemo(
      () => offer?._features?.isSravniOffer && isPromoActive && isMobile,
      [offer?._features?.isSravniOffer, isPromoActive, isMobile],
    );

    const btnLabel = (() => {
      if (isBankruptcyCard) {
        return 'Далее';
      }

      if (isMobile && detailsButtonProps?.visibleOnMobile) {
        return 'Получить';
      }

      return undefined;
    })();

    const actionHandler = useCardAction(onContentClickCapture, {
      isMobile,
      offer,
      position,
      hasUtmLabel,
    });

    return (
      <div className={cn(styles.cardWrapper, className)}>
        <div
          className={cn(styles.card, styles.link, className, { [styles.fullDealCard]: isFullDealCard })}
          data-qa="Card"
        >
          <div className={styles.content} onClickCapture={actionHandler}>
            {isFullDealCard && (
              <AdaptiveText
                longText="Дарим 500₽ за займ"
                shortText="Дарим 500₽ за займ"
                renderText={(props) => (
                  <Typography.Text size={12} {...props} className={cn(styles.fullDealLabel, props.className)} />
                )}
              />
            )}
            {/* eslint-disable-next-line jsx-a11y/anchor-is-valid */}
            <Link className={styles.link_content} link={cardLink} analytics={offer}>
              <div className={styles.header}>
                <div className={styles.text}>
                  <Typography.Heading className={styles.text} level={6} as="div">
                    {organization.name}
                  </Typography.Heading>
                </div>
              </div>
              {!!badge && <div>{badge}</div>}
              {!isBankruptcyCard ? <div className={styles.space} /> : null}
              <div className={styles.card_content}>
                {pixelDisplayLink && <Pixel source={sourceAffI} link={pixelDisplayLink} affSub4={affSub4} />}
                {isBankruptcyCard ? (
                  <ContentBankruptcy />
                ) : (
                  <div className={styles.content_row}>
                    <ContentItem title="Сумма до" data={amount} />

                    {!isLanding && (
                      <ContentItem
                        classNameTitle={styles.content_item_title_special}
                        classNameData={styles.content_item_special}
                        title="Срок (дни)"
                        data={isTemporaryTerm ? getTemporaryTerm(organization._id, term) : term}
                        wrapDataByString={littleMode ? ' до ' : undefined}
                      />
                    )}

                    <ContentItem
                      data={
                        <CardApprovalProbability isNewDesignCard={false} category={approvalProbability?.category} />
                      }
                      title="Одобрение"
                    />
                  </div>
                )}
              </div>
            </Link>
            <Logo
              className={styles.header_logo}
              link={logoLink}
              analytics={offer}
              logo={organization.logotypes?.android || organization.logotypes?.square}
              isPartner={offer.isBankPartner || offer.advertising?.isPartner}
              size={isMobile ? 36 : 44}
            />
          </div>

          <div className={styles.actions}>
            {detailsButtonProps && !isBankruptcyCard && (
              <Button
                variant="secondary"
                onClick={detailsButtonProps?.onClick}
                className={cn(styles.detailsButton, {
                  [styles.hiddenFromMobile]: !detailsButtonProps?.visibleOnMobile,
                })}
              >
                <Icon icon={<Tip />} />
              </Button>
            )}
            {/* добавить возможность вызывать свой экшен */}

            <CardActionButton
              onClick={actionHandler}
              className={styles.button}
              actionButton={actionButton}
              source={sourceAffC}
              offer={offer}
              affSub4={affSub4}
              freeButtonLink={freeButtonLink}
              visibleLabel={btnLabel}
            />
          </div>
          {/* todo(MFO-1414): временно (а может и нет) скрыли рекламу */}
          {/* {isAdvertisingInfoVisible && (
                          <Typography.Text className={styles.disclaimer}>
                              Реклама. {offer.organization.fullName || offer.organization.name}
                          </Typography.Text>
                      )} */}
        </div>

        {afterCard}
      </div>
    );
  },
);

Card.displayName = 'Card';

export default Card;
